import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-20">
        <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-200 mb-4">
          <i className="ri-file-search-line text-3xl text-foreground-400"></i>
        </div>
        <h2 className="font-heading text-xl font-semibold text-foreground-800 mb-2">文章未找到</h2>
        <p className="text-foreground-500 text-sm mb-6">该文章不存在或已下架</p>
        <Link
          to="/"
          className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.summary,
    image: article.image,
    datePublished: article.date,
    author: {
      "@type": "Organization",
      name: "17.c 起草编辑部",
    },
    publisher: {
      "@type": "Organization",
      name: "17.c 起草在线观看",
      url: "https://17c-drafting.example.com",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://17c-drafting.example.com/article/${id}`,
    },
  };

  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <main className="pt-16">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Breadcrumb
        items={[
          { label: "首页", href: "/" },
          { label: "文章列表", href: "/" },
          { label: article.title },
        ]}
      />

      <article className="py-8 md:py-10 bg-background-50">
        <div className="max-w-[800px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full mb-4">
              {article.tag}
            </span>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i> {article.views} 阅读
              </span>
              <span>17.c 起草编辑部</span>
            </div>
          </div>

          <div className="w-full h-[340px] md:h-[400px] rounded-xl overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed">
            {article.content.split("\n").filter(Boolean).map((paragraph, index) => {
              const trimmed = paragraph.trim();
              if (trimmed.startsWith("<h2>")) {
                return <h2 key={index} className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-8 mb-4">{trimmed.replace(/<\/?h2>/g, "")}</h2>;
              }
              if (trimmed.startsWith("<p>")) {
                return <p key={index} className="mb-4 text-foreground-600 leading-relaxed">{trimmed.replace(/<\/?p>/g, "")}</p>;
              }
              return <p key={index} className="mb-4 text-foreground-600 leading-relaxed">{trimmed}</p>;
            })}
          </div>

          <div className="mt-10 pt-8 border-t border-background-200">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-500">分享文章：</span>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                  <i className="ri-wechat-line text-sm"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                  <i className="ri-weibo-line text-sm"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                  <i className="ri-link text-sm"></i>
                </a>
              </div>
              <div>
                <Link
                  to="/"
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i> 返回首页
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="py-10 md:py-14 bg-background-100/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">
              相关文章推荐
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-[960px] mx-auto">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.id}`}
                  className="group rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="w-full h-[160px] overflow-hidden">
                    <img src={ra.image} alt={ra.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-foreground-900 group-hover:text-primary-600 text-sm transition-colors line-clamp-2 mb-2">
                      {ra.title}
                    </h3>
                    <span className="text-xs text-foreground-400">{ra.date}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}